// src/context/AppContext.tsx
import React, { createContext, useContext, useEffect, useState } from "react";
import { getProfile } from "../api_v2/auth";
import { getToken } from "../api_v2/client";
import { platformApiUrl } from "../config/api";

const STATE_KEY = "simolapps_state";

type UserRecord = {
  id: number;
  name?: string;
  first_name?: string;
  last_name?: string;
  phone?: string;
  subscription_status?: string;
  subscription?: Record<string, any> | null;
  [key: string]: any;
};
type User = UserRecord | null;
type City = { id: number; name: string } | null;

interface Ctx {
  user: User;
  setUser: (u: User) => void;
  city: City;
  setCity: (c: City) => void;
  prayers: Record<string, any>[];
  setPrayers: (p: Record<string, any>[]) => void;
  ready: boolean;
}

const AppContext = createContext<Ctx | undefined>(undefined);
export const useApp = () => {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useApp must be used inside <AppProvider>");
  return ctx;
};

export const AppProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User>(null);
  const [city, setCity] = useState<City>(null);
  const [prayers, setPrayers] = useState<Record<string, any>[]>([]);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const restoreLegacyUser = (): User => {
      const raw = localStorage.getItem(STATE_KEY);
      if (raw) {
        try {
          const st = JSON.parse(raw);
          if (st?.user?.id) {
            if (Array.isArray(st.prayers)) setPrayers(st.prayers);
            return {
              ...st.user,
              id: Number(st.user.id),
              name:
                st.user.name ||
                [st.user.first_name, st.user.last_name].filter(Boolean).join(" ").trim() ||
                "User",
            };
          }
          if (Array.isArray(st?.prayers)) setPrayers(st.prayers);
        } catch {
          /* ignore */
        }
      }

      const uid = localStorage.getItem("userId");
      if (uid) {
        return { id: Number(uid), name: localStorage.getItem("userName") || "User" };
      }

      return null;
    };

    const restoreCity = () => {
      const savedCity = localStorage.getItem("city");
      if (!savedCity) return;
      try {
        const parsed = JSON.parse(savedCity);
        if (parsed?.id && parsed?.name) {
          setCity(parsed);

          fetch(`${platformApiUrl("namaz/prayer-times")}?city_id=${parsed.id}`)
            .then(r => r.json())
            .then(setPrayers)
            .catch((err) => console.error("Ошибка загрузки prayer_times:", err));
        }
      } catch (err) {
        console.error("Ошибка парсинга сохранённого города:", err);
      }
    };

    const persistBootUser = (bootUser: UserRecord) => {
      const normalized = {
        ...bootUser,
        id: Number(bootUser.id),
        name:
          bootUser.name ||
          [bootUser.first_name, bootUser.last_name].filter(Boolean).join(" ").trim() ||
          "User",
      };
      setUser(normalized);

      let st: any = {};
      try {
        st = JSON.parse(localStorage.getItem(STATE_KEY) || "{}");
      } catch {
        st = {};
      }
      st.user = { ...(st.user || {}), ...normalized };
      st.isRegistered = true;
      localStorage.setItem(STATE_KEY, JSON.stringify(st));
      localStorage.setItem("userId", String(normalized.id));
      localStorage.setItem("userName", normalized.name || "");
    };

    const bootstrap = async () => {
      restoreCity();

      const token = getToken();
      if (token) {
        try {
          const { success, user: profile } = await getProfile();
          if (success && profile?.id) {
            persistBootUser(profile as UserRecord);
            setReady(true);
            return;
          }
        } catch {
          /* fall back to legacy state if v2 API is temporarily unavailable */
        }
      }

      setUser(restoreLegacyUser());
      setReady(true);
    };

    bootstrap();
  }, []);

  const _setUser = (u: User) => {
    setUser(u);
    if (!u) {
      localStorage.removeItem("userId");
      localStorage.removeItem("userName");
      localStorage.removeItem(STATE_KEY);
      return;
    }
    localStorage.setItem("userId", String(u.id));
    const displayName =
      u.name || [u.first_name, u.last_name].filter(Boolean).join(" ").trim() || "";
    localStorage.setItem("userName", displayName);
    const prev = localStorage.getItem(STATE_KEY);
    let st: any = {};
    try { st = prev ? JSON.parse(prev) : {}; } catch {}
    st.user = { ...(st.user || {}), ...u };
    localStorage.setItem(STATE_KEY, JSON.stringify(st));
  };

  useEffect(() => {
    const prev = localStorage.getItem(STATE_KEY);
    let st: any = {};
    try { st = prev ? JSON.parse(prev) : {}; } catch {}
    st.prayers = prayers;
    localStorage.setItem(STATE_KEY, JSON.stringify(st));
  }, [prayers]);

  return (
    <AppContext.Provider value={{ user, setUser: _setUser, city, setCity, prayers, setPrayers, ready }}>
      {children}
    </AppContext.Provider>
  );
};
